import { Icon } from "@uzverse/ui";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ownerConnectionsUrl } from "../config.js";
import { useSession } from "../state/session.js";
import type { TaskItem } from "../state/store.js";

/** Diagnosis prefix the orchestrator writes when no connection could serve the work. */
export const EXHAUSTED_PREFIX = "exhausted:";

/** True while a task waits for an AI connection (it resumes by itself once one exists). */
export function isPausedForConnection(task: Pick<TaskItem, "state" | "detail">): boolean {
  return task.state === "needs_owner" && (task.detail ?? "").startsWith(EXHAUSTED_PREFIX);
}

/**
 * The chat card for a task paused for lack of a connection: the reason, and the next step.
 * Owners get a link to the owner panel's Connections page for the task's company; everyone else
 * is told that the owner has been notified.
 */
export function PausedCard({ task, isOwner }: { task: TaskItem; isOwner: boolean }) {
  const { t } = useTranslation("web");
  const { services } = useSession();
  const [fallbackCompany, setFallbackCompany] = useState<string | null>(null);
  const needsLookup = isOwner && !task.companyId;
  useEffect(() => {
    if (!needsLookup) return;
    let live = true;
    void (async () => {
      try {
        const orgs = await services.api.listOrganization();
        if (live) setFallbackCompany(orgs[0]?.id ?? null);
      } catch {
        // No link without a company: the card still explains the pause.
      }
    })();
    return () => {
      live = false;
    };
  }, [needsLookup, services]);
  const href = isOwner ? ownerConnectionsUrl(task.companyId ?? fallbackCompany) : null;
  return (
    <div className="paused-card note note-warn" role="status" data-testid="paused-card">
      <p className="paused-title">
        <Icon name="alert" size={14} /> <strong>{t("paused.title")}</strong>
      </p>
      <p className="small">{t("paused.body")}</p>
      {isOwner ? (
        href && (
          <p>
            <a
              className="btn btn-sm btn-primary"
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              title={t("paused.ownerHint")}
            >
              {t("paused.ownerAction")}
            </a>
          </p>
        )
      ) : (
        <p className="muted small">{t("paused.memberNotified")}</p>
      )}
    </div>
  );
}
